import type { TypeLogisticsType, TypeOwnerType } from '~/constants/common';

export type TypePermission = {
  /** 화주사 유형 */
  ownerType: TypeOwnerType;
  /**
   * 물류 유형
   * @note 없으면 ownerType만 체크
   */
  logisticsTypes?: TypeLogisticsType[];
};

/** 권한 타입 */
export type TypePermissions = TypePermission[];

/**
 * 사용자의 권한을 확인하는 함수
 *
 * @param userOwnerType - 사용자의 화주사 유형
 * @param userLogisticsTypes - 사용자의 물류 유형 배열
 * @param permissions - 확인할 권한
 * @returns 권한이 있으면 true, 없으면 false
 *
 * @note
 * logisticsTypes 처리 방식
 * - `userLogisticsTypes = []` (빈 배열)
 *   - 모든 물류유형 권한 보유로 간주
 *   - 현재 토큰: FBK는 이 방식으로 오고 있음
 *   - 추후 FBK에도 logisticsTypes 세분화 예정
 *
 * - `userLogisticsTypes = ['FULFILLMENT', 'DELIVERY']` (모든 물류유형 명시)
 *   - 요구된 타입 중 하나라도 포함되면 통과
 *   - 현재 토큰: 3PL, NFA는 이 방식으로 오고 있음
 */
const checkPermission = (
  userOwnerType: TypeOwnerType,
  userLogisticsTypes: TypeLogisticsType[],
  permissions: TypePermissions
): boolean => {
  if (!userOwnerType) {
    return false;
  }

  const ownerPermission = permissions.find((permission) => permission.ownerType === userOwnerType);

  // ownerType 권한 체크
  if (!ownerPermission) {
    return false;
  }

  // logisticsTypes가 없으면 ownerType만으로 통과
  if (!ownerPermission.logisticsTypes || ownerPermission.logisticsTypes.length === 0) {
    return true;
  }

  // 사용자의 logisticsTypes가 비어있으면 모든 물류유형 권한 보유
  if (!userLogisticsTypes || userLogisticsTypes.length === 0) {
    return true;
  }

  // logisticsTypes 권한 체크: 필요한 물류유형 중 하나라도 사용자가 보유하고 있으면 통과
  return ownerPermission.logisticsTypes.some((type) => userLogisticsTypes.includes(type));
};

export default checkPermission;
